import { useMutation, useQuery } from '@tanstack/react-query'
import type { ImagingTestTemplateSingleResponse, ImagingTestTemplatesListResponse } from '@type/imagingTestTemp.type'
import { AXIOS_INSTANCE } from '../api'
import { queryClient } from '../store/provider/TanStackProvider'

type Props = {
  page?: number
  limit?: number
  query?: string
  category?: string
  slug?: string
  id?: string
}

export const useImagingTestTemp = (arg: Props = {}) => {
  const { page = 1, limit = 100, query, category, slug, id } = arg
  const { data, isLoading } = useQuery<ImagingTestTemplatesListResponse>({
    enabled: !id,
    queryKey: ['imaging_test_temp', { page, limit, query, category, slug }],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<ImagingTestTemplatesListResponse>('/imaging_test_templates', {
        params: {
          'page[number]': page,
          'page[size]': limit,
          ...(query && { query }),
          ...(category && { category }),
          ...(slug && { slug })
        }
      })
      return data
    }
  })

  const { data: singleImagingTestTemp, isLoading: singleImagingTestTempIsLoading } = useQuery<
    ImagingTestTemplateSingleResponse | undefined
  >({
    enabled: !!id,
    queryKey: ['imaging_test_temp', { id }],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<ImagingTestTemplateSingleResponse>(
        `/imaging_test_templates/${id as string}`
      )
      return data
    }
  })

  const { mutateAsync: createImagingTestTemp } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post('/imaging_test_templates', formVal, {
        headers: {
          'Content-Type': 'application/vnd.api+json'
        }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imaging_test_temp'] })
    }
  })

  const { mutateAsync: updateImagingTestTemp } = useMutation({
    mutationFn: async ({ id, formVal }: { id: string; formVal: any }) => {
      const { data } = await AXIOS_INSTANCE.patch(`/imaging_test_templates/${id}`, formVal, {
        headers: {
          'Content-Type': 'application/vnd.api+json'
        }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imaging_test_temp'] })
      queryClient.invalidateQueries({ queryKey: ['imaging_test_temp', { id }] })
    }
  })

  const { mutateAsync: softDeleteImagingTestTemp } = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await AXIOS_INSTANCE.delete(`/imaging_test_templates/${id}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imaging_test_temp'] })
      queryClient.invalidateQueries({ queryKey: ['imaging_test_temp', { id }] })
    }
  })

  return {
    data,
    pagination: data?.meta.pagination,
    isLoading,
    singleImagingTestTemp,
    singleImagingTestTempIsLoading,
    createImagingTestTemp,
    updateImagingTestTemp,
    softDeleteImagingTestTemp
  }
}
